import { useState, useEffect } from "react";
import { Cpu, MemoryStick, Brain, TrendingUp, AlertTriangle, CheckCircle, Clock, Zap, ChevronDown, ChevronUp } from "lucide-react";
import { useCpuRamAnalysis } from "../../../hooks/aiHooks/useCpuRamAnalysis";
import AIThinkingLoader from "../../common/AIThinkingLoader";

const AICpuRamAnalysisCard = ({ vmIds }) => {
  const [analysisData, setAnalysisData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { getCpuRamAnalysis } = useCpuRamAnalysis();

  useEffect(() => {
    if (vmIds && vmIds.length > 0) {
      fetchAnalysisData();
    }
  }, [vmIds]);

  const fetchAnalysisData = async () => {
    setLoading(true);
    setError(null);
    try {
      const results = [];
      for (const vmId of vmIds) {
        const result = await getCpuRamAnalysis(vmId);
        if (result.success && result.data) {
          results.push({
            vmId: vmId,
            vmName: result.data.vmName,
            cpuPatternDescription: result.data.cpuPatternDescription,
            cpuPeakDay: result.data.cpuPeakDay,
            cpuPeakHour: result.data.cpuPeakHour,
            cpuSeverity: result.data.cpuSeverity,
            ramPatternDescription: result.data.ramPatternDescription,
            ramSeverity: result.data.ramSeverity,
            anomalyDetected: result.data.anomalyDetected,
            anomalyDetails: result.data.anomalyDetails,
            recommendationAction: result.data.recommendationAction,
            recommendationSuggestion: result.data.recommendationSuggestion,
          });
        }
      }
      setAnalysisData(results);
    } catch (err) {
      console.error("Error fetching CPU/RAM analysis:", err);
      setError("Failed to load AI analysis data");
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toUpperCase()) {
      case "CRITICAL":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case "HIGH":
        return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "MEDIUM":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "LOW":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getAnomalyBadge = (detected) => {
    if (detected) {
      return { text: "Anomaly Detected", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: <AlertTriangle className="w-3 h-3" /> };
    }
    return { text: "No Anomaly", color: "bg-green-500/20 text-green-400 border-green-500/30", icon: <CheckCircle className="w-3 h-3" /> };
  };

  const getActionBadge = (action) => {
    switch (action?.toUpperCase()) {
      case "SCALE_UP":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "SCALE_DOWN":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "INVESTIGATE":
        return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const formatPeakTime = (day, hour) => {
    if (!day && hour === null) return "No pattern detected";
    if (!day) return `Around ${hour}:00`;
    if (hour === null) return day;
    return `${day} at ${hour}:00`;
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!vmIds || vmIds.length === 0) {
    return null;
  }

  if (loading) {
    return <AIThinkingLoader />;
  }

  if (error) {
    return (
      <div className="rounded-xl border border-primary-cyan/20 overflow-hidden mx-2 sm:mx-4 my-4" style={{ backgroundColor: "#10182b" }}>
        <div className="px-3 sm:px-5 py-3 border-b border-primary-cyan/20" style={{ backgroundColor: "#10182b" }}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-primary-cyan/20 flex items-center justify-center">
                <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-primary-cyan" />
              </div>
              <span className="text-primary-cyan text-xs sm:text-base font-bold tracking-wide">AI CPU/RAM INSIGHTS</span>
            </div>
          </div>
        </div>
        <div className="p-6 sm:p-8 text-center" style={{ backgroundColor: "#10182b" }}>
          <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-400 text-xs sm:text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (analysisData.length === 0) {
    return (
      <div className="rounded-xl border border-primary-cyan/20 overflow-hidden mx-2 sm:mx-4 my-4" style={{ backgroundColor: "#10182b" }}>
        <div className="px-3 sm:px-5 py-3 border-b border-primary-cyan/20" style={{ backgroundColor: "#10182b" }}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-primary-cyan/20 flex items-center justify-center">
                <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-primary-cyan" />
              </div>
              <span className="text-primary-cyan text-xs sm:text-base font-bold tracking-wide">AI CPU/RAM INSIGHTS</span>
            </div>
          </div>
        </div>
        <div className="p-6 sm:p-8 text-center" style={{ backgroundColor: "#10182b" }}>
          <p className="text-gray-500 text-xs sm:text-sm">No CPU/RAM analysis available for selected VMs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-primary-cyan/20 overflow-hidden mx-2 sm:mx-4 my-4" style={{ backgroundColor: "#10182b" }}>
      <div className="px-3 sm:px-5 py-3 border-b border-primary-cyan/20 sticky top-0 z-5" style={{ backgroundColor: "#10182b" }}>
        <div className="flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-primary-cyan/20 flex items-center justify-center">
              <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-primary-cyan" />
            </div>
            <span className="text-primary-cyan text-xs sm:text-base font-bold tracking-wide">AI CPU/RAM INSIGHTS</span>
          </div>
          <button
            onClick={toggleExpand}
            className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg bg-primary-cyan/10 hover:bg-primary-cyan/20 transition-all duration-200 text-primary-cyan text-xs sm:text-sm font-medium"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Show Less</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Show Details</span>
              </>
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-3 sm:p-5" style={{ backgroundColor: "#10182b" }}>
          <div className="space-y-4 sm:space-y-5">
            {analysisData.map((vm) => {
              const anomalyBadge = getAnomalyBadge(vm.anomalyDetected);
              return (
                <div key={vm.vmId} className="rounded-xl overflow-hidden border border-primary-cyan/20" style={{ backgroundColor: "#10182b" }}>
                  <div className="px-3 sm:px-5 py-3 border-b border-primary-cyan/20">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                      <h4 className="text-white font-bold text-lg sm:text-xl break-words">{vm.vmName}</h4>
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] sm:text-xs font-semibold ${anomalyBadge.color}`}>
                        {anomalyBadge.icon}
                        <span>{anomalyBadge.text}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 sm:p-5">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                      <div className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200" style={{ backgroundColor: "#1c2436" }}>
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                            <Cpu className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                          </div>
                          <span className="text-primary-cyan text-xs sm:text-sm font-bold uppercase tracking-wide">CPU Pattern</span>
                        </div>
                        <p className="text-gray-200 text-xs sm:text-sm leading-relaxed font-medium mb-2 sm:mb-3 break-words">
                          {vm.cpuPatternDescription || "No pattern detected"}
                        </p>
                        {(vm.cpuPeakDay || vm.cpuPeakHour !== null) && (
                          <p className="text-gray-400 text-[10px] sm:text-xs mt-2">
                            Peak: {formatPeakTime(vm.cpuPeakDay, vm.cpuPeakHour)}
                          </p>
                        )}
                        {vm.cpuSeverity && (
                          <div className="mt-2 sm:mt-3">
                            <span className={`text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full font-semibold ${getSeverityColor(vm.cpuSeverity)}`}>
                              {vm.cpuSeverity} Severity
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200" style={{ backgroundColor: "#1c2436" }}>
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                            <MemoryStick className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                          </div>
                          <span className="text-primary-cyan text-xs sm:text-sm font-bold uppercase tracking-wide">RAM Pattern</span>
                        </div>
                        <p className="text-gray-200 text-xs sm:text-sm leading-relaxed font-medium mb-2 sm:mb-3 break-words">
                          {vm.ramPatternDescription || "No pattern detected"}
                        </p>
                        {vm.ramSeverity && (
                          <div className="mt-2 sm:mt-3">
                            <span className={`text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full font-semibold ${getSeverityColor(vm.ramSeverity)}`}>
                              {vm.ramSeverity} Severity
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {vm.anomalyDetected && vm.anomalyDetails && (
                      <div className="mt-3 sm:mt-4 rounded-xl p-3 sm:p-4" style={{ backgroundColor: "#1c2436" }}>
                        <div className="flex items-center gap-2 sm:gap-3 mb-2">
                          <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 text-orange-400" />
                          <span className="text-orange-400 text-[10px] sm:text-xs font-bold uppercase tracking-wide">Anomaly Details</span>
                        </div>
                        <p className="text-gray-300 text-xs sm:text-sm break-words">{vm.anomalyDetails}</p>
                      </div>
                    )}

                    <div className="mt-3 sm:mt-4 rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200" style={{ backgroundColor: "#1c2436" }}>
                      <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                        </div>
                        <span className="text-primary-cyan text-xs sm:text-sm font-bold uppercase tracking-wide">Recommendation</span>
                      </div>
                      <p className="text-gray-200 text-sm sm:text-base leading-relaxed font-medium break-words">
                        {vm.recommendationSuggestion || "No recommendation available"}
                      </p>
                      {vm.recommendationAction && vm.recommendationAction !== "NONE" && (
                        <div className="mt-3 sm:mt-4">
                          <span className={`text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full font-semibold ${getActionBadge(vm.recommendationAction)}`}>
                            Action: {vm.recommendationAction.replace("_", " ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AICpuRamAnalysisCard;